// The *Unassigned voices* tab, connected (ADR-0077): the shared selection hook over the
// identity voices endpoints, which confirm the voices with the audited voice decisions, so a
// voice already confirmed elsewhere comes back as a 409 the tab shows as the server said it.
import type { QueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { canDecideFaces } from '@/features/face-matching/decision-role'
import { HUMINT_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import {
  identityVoicesApi,
  type ManualVoiceIdentity,
  type UnassignedVoice,
  type UnassignedVoicesPage,
} from '@/services/api/identity-voices'
import {
  type ManualIdentityNames,
  type UnassignedSelection,
  useUnassignedSelection,
} from '../hooks/useUnassignedSelection'
import { invalidateVoices, voiceKeys } from './keys'

/** One screenful of rows; the tab pages the rest. */
export const UNASSIGNED_VOICES_PAGE_SIZE = 25

export interface UseUnassignedVoicesResult
  extends UnassignedSelection<UnassignedVoicesPage, ManualVoiceIdentity> {
  voices: UnassignedVoice[]
  /** The caller may assign: a collaborator or an owner, as the read named them. */
  canDecide: boolean
}

export function useUnassignedVoices(caseId: string): UseUnassignedVoicesResult {
  const humint = useExtensionEnabled(HUMINT_EXTENSION_ID)
  // A write moves voices out of the tab and into an identity.
  const invalidate = useCallback(
    (queryClient: QueryClient) => {
      invalidateVoices(queryClient, caseId)
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'identities'] })
    },
    [caseId]
  )
  const selection = useUnassignedSelection<UnassignedVoicesPage, ManualVoiceIdentity>({
    queryKey: page => voiceKeys.unassignedPage(caseId, page),
    fetchPage: (page, pageSize) => identityVoicesApi.unassigned(caseId, { page, pageSize }),
    total: page => page.total,
    pageSize: UNASSIGNED_VOICES_PAGE_SIZE,
    enabled: Boolean(caseId) && humint.enabled,
    create: (names: ManualIdentityNames, selected) =>
      identityVoicesApi.createFromVoices(caseId, { ...names, voiceprint_ids: selected }),
    assign: (identityId, selected) => identityVoicesApi.assign(caseId, identityId, selected),
    invalidate,
  })
  return {
    ...selection,
    voices: selection.data?.voices ?? [],
    canDecide: selection.data !== undefined && canDecideFaces(selection.data.member_role),
  }
}
